import { useState, useEffect } from 'react';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { 
  Plus, DollarSign, Calendar, User, MoreVertical,
  ChevronRight, GripVertical, Edit, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const stages = [
  { id: 'new_lead', name: 'New Lead', color: 'blue' },
  { id: 'contacted', name: 'Contacted', color: 'yellow' },
  { id: 'proposal_sent', name: 'Proposal Sent', color: 'purple' },
  { id: 'negotiation', name: 'Negotiation', color: 'orange' },
  { id: 'closed_won', name: 'Closed Won', color: 'green' },
  { id: 'closed_lost', name: 'Closed Lost', color: 'red' },
];

const stageColors = {
  blue: 'border-blue-500/50 bg-blue-500/5',
  yellow: 'border-yellow-500/50 bg-yellow-500/5',
  purple: 'border-purple-500/50 bg-purple-500/5',
  orange: 'border-orange-500/50 bg-orange-500/5',
  green: 'border-green-500/50 bg-green-500/5',
  red: 'border-red-500/50 bg-red-500/5',
};

const CRMDeals = () => {
  const { token } = useCRMAuth();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [draggingDeal, setDraggingDeal] = useState(null);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      const response = await axios.get(`${API}/api/crm/deals/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPipeline(response.data.pipeline);
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const moveDeal = async (dealId, newStage) => {
    try {
      await axios.put(`${API}/api/crm/deals/${dealId}/move?new_stage=${newStage}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPipeline();
      toast.success('Deal moved!');
    } catch (error) {
      toast.error('Failed to move deal');
    }
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    try {
      await axios.delete(`${API}/api/crm/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deal deleted');
      fetchPipeline();
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const handleDragStart = (e, deal) => {
    setDraggingDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggingDeal && draggingDeal.stage !== stageId) {
      moveDeal(draggingDeal.deal_id, stageId);
    }
    setDraggingDeal(null);
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Deal Pipeline</h1>
            <p className="text-slate-400">Drag and drop deals between stages</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Deal
          </button>
        </div>

        {/* Pipeline Kanban */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => {
            const stageData = pipeline[stage.id] || { deals: [], count: 0, total_value: 0 };
            
            return (
              <div
                key={stage.id}
                className={`flex-shrink-0 w-80 rounded-xl border-2 ${stageColors[stage.color]}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="p-4 border-b border-slate-700/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{stage.name}</h3>
                      <span className="px-2 py-0.5 bg-slate-700/50 rounded-full text-xs text-slate-300">
                        {stageData.count}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    ${stageData.total_value?.toLocaleString() || 0}
                  </p>
                </div>

                {/* Deals List */}
                <div className="p-3 space-y-3 min-h-[200px]">
                  {stageData.deals.map((deal) => (
                    <div
                      key={deal.deal_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, deal)}
                      className={`bg-slate-800/80 border border-slate-700/50 rounded-lg p-4 cursor-grab active:cursor-grabbing hover:border-slate-600 transition-all ${
                        draggingDeal?.deal_id === deal.deal_id ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-white text-sm">{deal.title}</h4>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deleteDeal(deal.deal_id)}
                            className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="w-3 h-3 text-green-400" />
                          <span className="text-green-400 font-medium">
                            ${deal.value?.toLocaleString() || 0}
                          </span>
                        </div>
                        
                        {deal.probability > 0 && (
                          <div className="w-full bg-slate-700/50 rounded-full h-1.5">
                            <div 
                              className="bg-blue-500 h-1.5 rounded-full"
                              style={{ width: `${deal.probability}%` }}
                            ></div>
                          </div>
                        )}
                        
                        {deal.expected_close_date && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {new Date(deal.expected_close_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {stageData.deals.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      Drop deals here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Deal Modal */}
      {showAddModal && (
        <AddDealModal 
          token={token} 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => {
            setShowAddModal(false);
            fetchPipeline();
          }} 
        />
      )}
    </CRMLayout>
  );
};

// Add Deal Modal Component
const AddDealModal = ({ token, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    stage: 'new_lead',
    probability: 10,
    description: '',
    expected_close_date: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        value: formData.value ? parseFloat(formData.value) : 0,
        probability: parseInt(formData.probability),
        expected_close_date: formData.expected_close_date || null
      };

      await axios.post(`${API}/api/crm/deals`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deal created!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Add New Deal</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Deal Title *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Website Redesign Project"
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Value ($)</label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="10000"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData({...formData, probability: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({...formData, stage: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              >
                {stages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Expected Close</label>
              <input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData({...formData, expected_close_date: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
            <textarea
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Create Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CRMDeals;
